import React, { useMemo, useRef, useState } from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import type { StrapiCustomerRow, StrapiQuotationRow, StrapiQuotationUpdateData } from '../../api/strapiCrm';
import { strapiGetQuotation, strapiUpdateQuotation } from '../../api/strapiCrm';
import {
  CustomerFormDialog,
  type CustomerFormDialogCustomer,
} from '../CustomersPage/CustomerFormDialog';
import { customerApproveIssueLabels } from '../CustomersPage/customerCompleteness';
import type { StrapiUser } from '../../api/strapi';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../query/queryKeys';
import { useQuotationDetailQuery } from '../../query/hooks';
import {
  canShowCreateWorkOrder,
  canShowViewSamplingSchedules,
  canShowEditRequest,
  canShowEditRequestApprove,
  canShowEditRequestReject,
  canShowManajerApprove,
  canShowManajerReject,
  canShowSpvApprove,
  canShowPicApproveRevision,
  canShowPicCustomerApprove,
  canShowSpvReject,
  isConsultantOnlyQuotation,
  quotationRowNeedsWorkflowFinancialDetail,
} from './quotationWorkflow';
import { ConsultantScheduleGroupDialog } from './ConsultantScheduleGroupDialog';
import { ContractScheduleEventDialog } from './ContractScheduleEventDialog';
import {
  quotationGrandTotalFromRow,
  quotationRequiresManajerApproval,
} from './quotationGrandTotal';

type NpwpGateResult =
  | { ok: true; customers: StrapiCustomerRow[]; missing: StrapiCustomerRow[] }
  | { ok: false; message: string };

/** Pelanggan quotation. Konsultan dicek per grup. `missing` = data wajib pelanggan setuju belum lolos. */
function quotationCustomersForNpwpGate(row: StrapiQuotationRow): NpwpGateResult {
  const customers: StrapiCustomerRow[] = [];
  const seen = new Set<string>();

  function take(customer: StrapiCustomerRow | null | undefined, missingMessage: string): string | null {
    const documentId = customer?.documentId?.trim() ?? '';
    if (!documentId || !customer) return missingMessage;
    if (seen.has(documentId)) return null;
    seen.add(documentId);
    customers.push(customer);
    return null;
  }

  const groups = row.quotation_groups ?? [];
  if (row.is_consultant_quote && groups.length > 0) {
    for (let index = 0; index < groups.length; index += 1) {
      const message = take(groups[index]?.customer, `Pelanggan pada sampel ${index + 1} tidak terhubung.`);
      if (message) return { ok: false, message };
    }
  } else {
    const message = take(row.customer, 'Pelanggan tidak terhubung ke quotation ini.');
    if (message) return { ok: false, message };
  }

  return {
    ok: true,
    customers,
    missing: customers.filter((customer) => customerApproveIssueLabels(customer).length > 0),
  };
}

type WorkflowAction =
  | 'approve'
  | 'reject'
  | 'customer_approve'
  | 'pic_approve_revision'
  | 'edit_request'
  | 'edit_request_approve'
  | 'edit_request_reject'
  | 'manajer_approve'
  | 'manajer_reject';

const ACTION_META: Record<
  WorkflowAction,
  {
    title: string;
    /** Main quotation status to set; omit for edit-request flow (status utama tidak berubah). */
    status?: string;
    remarkLabel: string;
    remarkRequired: boolean;
  }
> = {
  approve: {
    title: 'Setujui quotation',
    status: 'approved',
    remarkLabel: 'Catatan persetujuan (opsional)',
    remarkRequired: false,
  },
  reject: {
    title: 'Tolak quotation',
    status: 'rejected',
    remarkLabel: 'Alasan penolakan',
    remarkRequired: true,
  },
  customer_approve: {
    title: 'Pelanggan setuju',
    status: 'customer_approved',
    remarkLabel: 'Catatan persetujuan pelanggan (opsional)',
    remarkRequired: false,
  },
  pic_approve_revision: {
    title: 'Setujui perubahan quotation',
    status: 'submitted',
    remarkLabel: 'Catatan persetujuan PIC (opsional)',
    remarkRequired: false,
  },
  edit_request: {
    title: 'Minta revisi',
    remarkLabel: 'Catatan revisi (apa yang ingin diubah)',
    remarkRequired: true,
  },
  edit_request_approve: {
    title: 'Setujui permintaan revisi',
    remarkLabel: 'Catatan review SPV (opsional)',
    remarkRequired: false,
  },
  edit_request_reject: {
    title: 'Tolak permintaan revisi',
    remarkLabel: 'Alasan penolakan permintaan revisi',
    remarkRequired: true,
  },
  manajer_approve: {
    title: 'Setujui quotation (manajer)',
    status: 'manajer_approved',
    remarkLabel: 'Catatan persetujuan manajer (opsional)',
    remarkRequired: false,
  },
  manajer_reject: {
    title: 'Tolak quotation (manajer)',
    status: 'rejected_by_manajer',
    remarkLabel: 'Alasan penolakan manajer',
    remarkRequired: true,
  },
};

type Props = {
  jwt: string | null;
  row: StrapiQuotationRow;
  user: StrapiUser | null;
  /** Footer halaman view: tombol sejajar dengan Tutup. */
  variant?: 'footer' | 'grid';
  onCreateWorkOrder?: () => void;
  onViewSamplingSchedules?: (row: StrapiQuotationRow) => void;
  onUpdated?: () => void;
  accessCodes?: readonly string[] | null;
};

export function QuotationWorkflowActions({
  jwt,
  row,
  user,
  variant = 'grid',
  onCreateWorkOrder,
  onViewSamplingSchedules,
  onUpdated,
  accessCodes,
}: Props) {
  const qc = useQueryClient();
  const needsFinancialDetail = quotationRowNeedsWorkflowFinancialDetail(row.progress_status);
  const financialDetailQ = useQuotationDetailQuery(
    jwt,
    needsFinancialDetail ? row.documentId : null,
  );
  const financialRow = financialDetailQ.data ?? row;
  const grandTotal = useMemo(() => {
    if (needsFinancialDetail && financialDetailQ.isLoading && !financialDetailQ.data) {
      return null;
    }
    return quotationGrandTotalFromRow(financialRow);
  }, [needsFinancialDetail, financialDetailQ.isLoading, financialDetailQ.data, financialRow]);
  const workflowGrandTotalOpts = useMemo(
    () => ({ grandTotal }),
    [grandTotal],
  );
  const [pending, setPending] = useState<WorkflowAction | null>(null);
  const [npwpCustomers, setNpwpCustomers] = useState<CustomerFormDialogCustomer[]>([]);
  const [npwpPendingIds, setNpwpPendingIds] = useState<string[]>([]);
  const [gateError, setGateError] = useState<string | null>(null);
  const checkingCustomerRef = useRef(false);
  const [consultantPickerOpen, setConsultantPickerOpen] = useState(false);
  const [contractPickerOpen, setContractPickerOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const iconOnly = variant === 'grid';
  const isFooter = variant === 'footer';

  function openAction(action: WorkflowAction) {
    setPending(action);
    setNote('');
    setError(null);
  }

  async function beginCustomerApprove() {
    if (!jwt || checkingCustomerRef.current) return;
    checkingCustomerRef.current = true;
    setGateError(null);
    try {
      const detail = await strapiGetQuotation(jwt, row.documentId);
      const gate = quotationCustomersForNpwpGate(detail);
      if (!gate.ok) {
        setGateError(gate.message);
        return;
      }
      if (gate.missing.length > 0) {
        const multi = detail.is_consultant_quote && gate.customers.length > 1;
        const shown = multi ? gate.customers : gate.missing;
        setNpwpCustomers(
          shown.map((customer, index) => ({
            documentId: customer.documentId,
            name:
              customer.name?.trim() ||
              customer.customer_number?.trim() ||
              `Pelanggan ${index + 1}`,
          })),
        );
        setNpwpPendingIds(gate.missing.map((customer) => customer.documentId));
        return;
      }
      openAction('customer_approve');
    } catch (e) {
      setGateError(e instanceof Error ? e.message : 'Gagal memeriksa data pelanggan.');
    } finally {
      checkingCustomerRef.current = false;
    }
  }

  function onCustomerNpwpSaved(savedId: string) {
    void qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, row.documentId) });
    void qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
    const remaining = npwpPendingIds.filter((id) => id !== savedId);
    setNpwpPendingIds(remaining);
    if (remaining.length === 0) {
      setNpwpCustomers([]);
      openAction('customer_approve');
    }
  }

  function closeNpwpDialog() {
    setNpwpCustomers([]);
    setNpwpPendingIds([]);
  }

  function closeDialog() {
    if (saving) return;
    setPending(null);
    setNote('');
    setError(null);
  }

  async function confirmAction() {
    if (!jwt || !pending) return;
    const meta = ACTION_META[pending];
    const trimmed = note.trim();
    if (meta.remarkRequired && !trimmed) {
      setError('Catatan wajib diisi.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const patch: StrapiQuotationUpdateData = {};

      if (pending === 'pic_approve_revision') {
        patch.progress_status = 'submitted';
        patch.edit_request_status = 'none';
        patch.request_date = row.request_date?.trim() || now;
        if (trimmed) patch.remark_approval = trimmed;
      } else if (pending === 'edit_request') {
        // Status utama tidak diubah — permintaan revisi memakai field paralel.
        patch.edit_request_status = 'pending';
        patch.remark_edit = trimmed;
        patch.remark_edit_review = null;
      } else if (pending === 'edit_request_approve') {
        patch.edit_request_status = 'approved';
        patch.remark_edit_review = trimmed || null;
      } else if (pending === 'edit_request_reject') {
        patch.edit_request_status = 'rejected';
        patch.remark_edit_review = trimmed;
      } else {
        patch.progress_status = meta.status as StrapiQuotationUpdateData['progress_status'];
        if (
          pending === 'approve' ||
          pending === 'customer_approve' ||
          pending === 'manajer_approve'
        ) {
          patch.remark_approval = trimmed || null;
        } else {
          patch.remark_edit = trimmed || null;
        }
        if (pending === 'approve') {
          patch.spv_approved_date = now;
          const fullRow = await strapiGetQuotation(jwt, row.documentId);
          const approveGrandTotal = quotationGrandTotalFromRow(fullRow);
          patch.progress_status = quotationRequiresManajerApproval(approveGrandTotal)
            ? 'need_manajer_approval'
            : 'approved';
        }
        if (pending === 'manajer_approve') patch.manajer_approved_date = now;
        if (pending === 'customer_approve') patch.customer_approved_date = now;
      }

      await strapiUpdateQuotation(jwt, row.documentId, patch);
      await qc.invalidateQueries({ queryKey: queryKeys.quotationDetail(jwt, row.documentId) });
      await qc.invalidateQueries({ queryKey: queryKeys.quotationsListPrefix(jwt) });
      setPending(null);
      setNote('');
      onUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memperbarui status.');
    } finally {
      setSaving(false);
    }
  }

  const showReject = canShowSpvReject(row, user);
  const showApprove = canShowSpvApprove(row, user);
  const showCustomer = canShowPicCustomerApprove(row, user, workflowGrandTotalOpts);
  const showPicApproveRevision = canShowPicApproveRevision(row, user);
  const showManajer = canShowManajerApprove(row, user, workflowGrandTotalOpts);
  const showManajerReject = canShowManajerReject(row, user);
  const showEditReq = canShowEditRequest(row, user);
  const showEditReqApprove = canShowEditRequestApprove(row, user);
  const showEditReqReject = canShowEditRequestReject(row, user);
  const showWo = canShowCreateWorkOrder(row, user);
  const showSchedules = canShowViewSamplingSchedules(row, user, accessCodes);

  if (
    !showApprove &&
    !showReject &&
    !showCustomer &&
    !showPicApproveRevision &&
    !showManajer &&
    !showManajerReject &&
    !showEditReq &&
    !showEditReqApprove &&
    !showEditReqReject &&
    !showWo &&
    !showSchedules
  ) {
    return null;
  }

  const btnSize = isFooter ? 'medium' : 'small';

  function renderActionControl(
    label: string,
    onClick: () => void,
    options: {
      testId: string;
      icon: React.ReactElement;
      buttonVariant?: 'contained' | 'outlined';
      buttonColor?: 'success' | 'error' | 'primary' | 'inherit';
    },
  ) {
    if (iconOnly) {
      return (
        <Tooltip key={options.testId} title={label}>
          <IconButton
            size="small"
            aria-label={label}
            onClick={onClick}
            color={options.buttonColor === 'inherit' ? 'default' : options.buttonColor}
            data-testid={options.testId}
          >
            {options.icon}
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Button
        key={options.testId}
        size={btnSize}
        variant={options.buttonVariant ?? 'outlined'}
        color={options.buttonColor ?? 'primary'}
        onClick={onClick}
        data-testid={options.testId}
      >
        {label}
      </Button>
    );
  }

  const actionButtons = (
    <>
      {showReject
        ? renderActionControl('Tolak', () => openAction('reject'), {
            testId: `quotation-action-reject-${row.documentId}`,
            icon: <CancelOutlinedIcon fontSize="small" />,
            buttonVariant: 'outlined',
            buttonColor: 'error',
          })
        : null}
      {showApprove
        ? renderActionControl('Setujui', () => openAction('approve'), {
            testId: `quotation-action-approve-${row.documentId}`,
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'success',
          })
        : null}
      {showCustomer
        ? renderActionControl('Pelanggan setuju', () => void beginCustomerApprove(), {
            testId: `quotation-action-customer-approve-${row.documentId}`,
            icon: <HowToRegOutlinedIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'primary',
          })
        : null}
      {showPicApproveRevision
        ? renderActionControl('Setuju', () => openAction('pic_approve_revision'), {
            testId: `quotation-action-pic-approve-revision-${row.documentId}`,
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'success',
          })
        : null}
      {showManajerReject
        ? renderActionControl('Tolak (manajer)', () => openAction('manajer_reject'), {
            testId: `quotation-action-manajer-reject-${row.documentId}`,
            icon: <CancelOutlinedIcon fontSize="small" />,
            buttonVariant: 'outlined',
            buttonColor: 'error',
          })
        : null}
      {showManajer
        ? renderActionControl('Setujui (manajer)', () => openAction('manajer_approve'), {
            testId: `quotation-action-manajer-approve-${row.documentId}`,
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'success',
          })
        : null}
      {showEditReq
        ? renderActionControl('Minta revisi', () => openAction('edit_request'), {
            testId: `quotation-action-edit-request-${row.documentId}`,
            icon: <RateReviewOutlinedIcon fontSize="small" />,
            buttonVariant: 'outlined',
            buttonColor: 'inherit',
          })
        : null}
      {showEditReqReject
        ? renderActionControl('Tolak revisi', () => openAction('edit_request_reject'), {
            testId: `quotation-action-edit-request-reject-${row.documentId}`,
            icon: <CancelOutlinedIcon fontSize="small" />,
            buttonVariant: 'outlined',
            buttonColor: 'error',
          })
        : null}
      {showEditReqApprove
        ? renderActionControl('Setujui revisi', () => openAction('edit_request_approve'), {
            testId: `quotation-action-edit-request-approve-${row.documentId}`,
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'success',
          })
        : null}
      {showSchedules && (onViewSamplingSchedules || isConsultantOnlyQuotation(row) || row.is_contract)
        ? renderActionControl('Penjadwalan sampling', () => {
            if (row.is_contract) {
              setContractPickerOpen(true);
              return;
            }
            if (isConsultantOnlyQuotation(row)) {
              setConsultantPickerOpen(true);
              return;
            }
            onViewSamplingSchedules?.(row);
          }, {
            testId: `quotation-action-schedules-${row.documentId}`,
            icon: <CalendarMonthOutlinedIcon fontSize="small" />,
            buttonVariant: 'outlined',
            buttonColor: 'primary',
          })
        : null}
      {showWo && onCreateWorkOrder
        ? renderActionControl('Buat work order', onCreateWorkOrder, {
            testId: `quotation-action-work-order-${row.documentId}`,
            icon: <WorkOutlineOutlinedIcon fontSize="small" />,
            buttonVariant: 'contained',
            buttonColor: 'primary',
          })
        : null}
    </>
  );

  return (
    <>
      {isFooter ? (
        actionButtons
      ) : (
        <Stack direction="row" flexWrap="wrap" gap={iconOnly ? 0.25 : 1} useFlexGap alignItems="center">
          {actionButtons}
        </Stack>
      )}

      <Dialog open={pending != null} onClose={closeDialog} maxWidth="sm" fullWidth>
        {pending ? (
          <>
            <DialogTitle>{ACTION_META[pending].title}</DialogTitle>
            <DialogContent>
              {pending === 'pic_approve_revision' ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Setelah Anda setuju, quotation diajukan ke SPV marketing. Jika total ≥ Rp 20.000.000,
                  setelah SPV setuju akan lanjut ke manajer marketing.
                </Alert>
              ) : null}
              {pending === 'edit_request' ||
              pending === 'edit_request_approve' ||
              pending === 'edit_request_reject' ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {pending === 'edit_request'
                    ? 'Status quotation utama tidak berubah. SPV akan mereview catatan revisi Anda terlebih dahulu.'
                    : pending === 'edit_request_approve'
                      ? 'Jika disetujui, PIC dapat mengubah quotation. Status utama tetap sama sampai PIC mengajukan ulang.'
                      : 'Jika ditolak, status quotation utama tetap seperti semula dan PIC tidak dapat mengedit.'}
                </Alert>
              ) : null}
              {(pending === 'edit_request_approve' ||
                pending === 'edit_request_reject' ||
                pending === 'pic_approve_revision') &&
              (row.remark_edit ?? '').trim() ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Catatan: {(row.remark_edit ?? '').trim()}
                </Alert>
              ) : null}
              <TextField
                label={ACTION_META[pending].remarkLabel}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                margin="normal"
                required={ACTION_META[pending].remarkRequired}
                data-testid="quotation-workflow-remark"
              />
              {pending === 'approve' &&
              grandTotal != null &&
              quotationRequiresManajerApproval(grandTotal) ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Total quotation ≥ Rp 20.000.000 — setelah disetujui SPV, quotation menunggu persetujuan
                  manajer marketing.
                </Alert>
              ) : null}
              {pending === 'approve' && needsFinancialDetail && financialDetailQ.isLoading ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Menghitung total quotation…
                </Alert>
              ) : null}
              {error ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={saving}>
                Batal
              </Button>
              <Button
                variant="contained"
                onClick={() => void confirmAction()}
                disabled={
                  saving ||
                  (pending === 'approve' && needsFinancialDetail && financialDetailQ.isLoading)
                }
                data-testid="quotation-workflow-submit"
              >
                {saving ? 'Menyimpan…' : 'Submit'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
      <CustomerFormDialog
        key={npwpCustomers.map((customer) => customer.documentId).join('|') || 'closed'}
        open={npwpCustomers.length > 0}
        documentId={npwpPendingIds[0] ?? npwpCustomers[0]?.documentId ?? null}
        customers={npwpCustomers.length > 1 ? npwpCustomers : undefined}
        pendingIds={npwpPendingIds}
        jwt={jwt}
        onClose={closeNpwpDialog}
        onSaved={onCustomerNpwpSaved}
      />
      <Dialog open={gateError != null} onClose={() => setGateError(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Data pelanggan</DialogTitle>
        <DialogContent>
          <Alert severity="warning">{gateError}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGateError(null)}>Tutup</Button>
        </DialogActions>
      </Dialog>
      {isConsultantOnlyQuotation(row) ? (
        <ConsultantScheduleGroupDialog
          open={consultantPickerOpen}
          jwt={jwt}
          quotationDocumentId={row.documentId}
          onClose={() => setConsultantPickerOpen(false)}
          onEnsured={onUpdated}
        />
      ) : null}
      {row.is_contract ? (
        <ContractScheduleEventDialog
          open={contractPickerOpen}
          jwt={jwt}
          quotationDocumentId={row.documentId}
          onClose={() => setContractPickerOpen(false)}
          onEnsured={onUpdated}
        />
      ) : null}
    </>
  );
}
